// frontend/src/components/analyze/toolMeta.tsx
// Presentational metadata per Analyze tool — icons, colors, titles, copy.
// Status logic lives in lib/analyzeToolState.ts; this file is looks only.
import type { ReactNode } from "react";
import {
  BarChart3,
  FileText,
  Globe2,
  MessageSquare,
  Scale,
  Swords,
} from "lucide-react";
import type { ToolId, ToolStatus } from "@/lib/analyzeToolState";

export interface ToolMeta {
  title: string;
  /** Short copy for tight surfaces (rail nest rows). */
  description: string;
  /** Fuller value proposition for the Overview cards — why you'd run this. */
  value: string;
  icon: ReactNode;
  iconColor: string;
  /** Rows that leave the Analyze surface get a ↗ handoff marker. */
  handoff?: boolean;
}

// Order is deliberate: the two corpus-wide diagnostics lead (debate, audit),
// then the focused/interactive tools, then the two ↗ handoffs.
export const TOOL_ORDER: ToolId[] = [
  "field-debate",
  "coverage-audit",
  "comparison",
  "chat",
  "landscape",
  "critique",
];

export const TOOL_META: Record<ToolId, ToolMeta> = {
  "field-debate": {
    title: "Field Debate",
    description: "Map camps, tensions, and silences.",
    value:
      "See where your field agrees, disagrees, and stays silent — camps, tensions, and bridge papers mapped from your scored corpus.",
    icon: <Swords className="size-3.5" />,
    iconColor: "#cba6f7",
  },
  chat: {
    title: "Ask the corpus",
    description: "Chat with your indexed papers.",
    value:
      "Ask questions in plain language and get answers grounded in your papers, with citations back to the source.",
    icon: <MessageSquare className="size-3.5" />,
    iconColor: "#94e2d5",
  },
  comparison: {
    title: "Comparison",
    description: "Side-by-side compare focus, method, findings.",
    value:
      "Line up selected papers side-by-side — research focus, methods, and findings in one table, with a synthesis.",
    icon: <FileText className="size-3.5" />,
    iconColor: "#a6e3a1",
  },
  "coverage-audit": {
    title: "Coverage Audit",
    description: "Cluster your corpus and find where evidence is thin.",
    value:
      "Stress-test your corpus for blind spots — clusters your papers and flags where your evidence is thin, with queries to fill the gaps.",
    icon: <BarChart3 className="size-3.5" />,
    iconColor: "#74c7ec",
  },
  landscape: {
    title: "Landscape",
    description: "Field as a 2D map.",
    value:
      "Fly over your field as a 2D map — see clusters, outliers, and where your corpus concentrates or thins out.",
    icon: <Globe2 className="size-3.5" />,
    iconColor: "#fab387",
    handoff: true,
  },
  critique: {
    title: "Argument Critique",
    description: "Stress-test your argument and place it in the field.",
    value:
      "Critique your own argument — weak claims, contradictions, and logical gaps, plus where it sits in the field's debate.",
    icon: <Scale className="size-3.5" />,
    iconColor: "#cba6f7",
    handoff: true,
  },
};

/** Badge styles ported from AnalyzeToolRail's STATUS_STYLES (visual continuity). */
export const STATUS_BADGE: Record<ToolStatus, { className: string; label: string }> = {
  ready: { className: "bg-emerald-500/15 text-emerald-400", label: "READY" },
  locked: { className: "bg-muted-foreground/15 text-muted-foreground", label: "LOCKED" },
  done: { className: "bg-blue-500/15 text-blue-400", label: "DONE" },
  stale: { className: "bg-amber-500/15 text-amber-400", label: "STALE" },
  running: { className: "bg-violet-500/15 text-violet-400", label: "RUNNING" },
  refreshing: { className: "bg-sky-500/15 text-sky-400", label: "REFRESHING" },
};
